import { Button, Col, Row, Spinner, Modal } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Back from "../../assets/svg/back.svg";
import SearchWithAPI from "../common/search-box-new";
import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import CommonDropdown from "../common/common-new-dropdown.jsx";
import { ReactComponent as DeleteIC } from "../../assets/svg/delete-po.svg";
import { ReactComponent as AddNew } from "../../assets/svg/add_new.svg";
import { FaInfoCircle } from "react-icons/fa";
import DOMPurify from "dompurify";
import { sanitizeInput, sanitizeEmail, sanitizePhoneNumber, sanitizeNumericInput } from "../common/security-utils";
import { applyStateTaxFlags } from "../common/state-tax";
import { createQuotationApi, getCustomerDetailsApi, getLeadDetailsApi, getQuotationDetailsApi, updateQuotationApi, addQuotationAttachmentsBulkApi, getQuotationAttachmentsApi, removeQuotationAttachmentApi, getOrgCurrencyApi } from "./quotation-api.js";
import SearchWithFreeTextAPI from "../common/searchbox-free-text";
import FileUploadComponent from "../common/FileUploadComponent";
import AttachmentDeleteModal from "../common/AttachmentDeleteModal";
import "./quotation.css";

// ⓘ icon with a breakdown tooltip rendered in a body portal so it is never
// clipped by the table's overflow container. `rows` is an array of
// [label, value] pairs shown above the icon.
const InfoTooltip = ({ rows }) => {
  const iconRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const openTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      // Anchor the tooltip's bottom-right just above the icon.
      setPos({ top: rect.top - 8, left: rect.right });
      setShow(true);
    }
  };
  const closeTooltip = () => setShow(false);

  return (
    <span
      ref={iconRef}
      className="total-info-wrap"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <FaInfoCircle className="total-info-icon" size={13} />
      {show &&
        createPortal(
          <span
            className="total-info-tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {rows.map(([label, value, isTotal], i) => (
              <span
                key={i}
                className={`tt-row${isTotal ? " tt-total" : ""}`}
              >
                <span>{label}</span>
                <span>{value}</span>
              </span>
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
};

// Tooltip badge for modified fields showing inline (Old -> New) on hover
const ChangeBadgeWithTooltip = ({ change }) => {
  const badgeRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const label = typeof change === "string" ? change : change.label;
  const fromVal = typeof change === "object" ? change.from : null;
  const toVal = typeof change === "object" ? change.to : null;

  const handleMouseEnter = () => {
    const rect = badgeRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
      setShow(true);
    }
  };

  const handleMouseLeave = () => {
    setShow(false);
  };

  return (
    <>
      <span
        ref={badgeRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundColor: "#FFFFFF",
          color: "#3D4AF5",
          border: "1px solid #D1D5FF",
          borderRadius: "6px",
          padding: "3px 10px",
          fontSize: "12px",
          fontWeight: 500,
          cursor: fromVal && toVal ? "pointer" : "default",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          userSelect: "none",
          transition: "all 0.15s ease",
          boxShadow: show ? "0 2px 8px rgba(61, 74, 245, 0.2)" : "none",
        }}
      >
        <span>•</span>
        <span>{label}</span>
      </span>

      {show &&
        fromVal &&
        toVal &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              transform: "translate(-50%, -100%)",
              zIndex: 99999,
              backgroundColor: "#0F172A",
              color: "#FFFFFF",
              borderRadius: "8px",
              padding: "7px 12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
              fontSize: "12px",
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              maxWidth: "420px",
              pointerEvents: "none",
              border: "1px solid #334155",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "nowrap",
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  color: "#E2E8F0",
                  fontWeight: 500,
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  verticalAlign: "bottom",
                }}
                title={fromVal}
              >
                {fromVal}
              </span>
              <span
                style={{
                  color: "#60A5FA",
                  fontWeight: 700,
                  fontSize: "13px",
                  lineHeight: 1,
                  margin: "0 2px",
                }}
              >
                &rarr;
              </span>
              <span
                style={{
                  color: "#34D399",
                  fontWeight: 600,
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  verticalAlign: "bottom",
                }}
                title={toVal}
              >
                {toVal}
              </span>
            </div>
            {/* Arrow */}
            <div
              style={{
                position: "absolute",
                bottom: "-5px",
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "5px solid transparent",
                borderRight: "5px solid transparent",
                borderTop: "5px solid #0F172A",
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

const QuotationCreate = () => {
  const location = useLocation();
  const { id, is_negiotate, moduleDetail, callFrom, fromView } = location.state || {};

  // 🔒 VAPT Security: Validate ID parameter
  const isValidId = useCallback((id) => {
    if (!id) return false;
    // Allow alphanumeric, hyphens, and underscores only
    return /^[a-zA-Z0-9_-]+$/.test(String(id));
  }, []);

  // 🔒 Security Constants
  const MAX_NUMERIC_VALUE = 999999999;

  //API URL
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiAccUrl = process.env.REACT_APP_ADMIN_API_URL;

  const navigate = useNavigate();
  const [itemType, setItemType] = useState("Product");
  const [productList, setProductList] = useState([
    // {
    //   id: 0,
    //   product: null,
    //   unit: null,
    //   quantity: 0,
    //   unit_without_tax: 0,
    //   cgst_percentage: 0,
    //   sgst_percentage: 0,
    //   igst_percentage: 0,
    //   total_amount: 0,
    // },
  ]);

  const [typeValue, setType] = useState("Customer");
  const handleTypeChange = (e) => {
    setType(e.target.value);
    setValidations({
      customer_id: "",
      item_type: "",
      title: "",
      valid_date: "",
      sales_person_id: "",
      quotation_date: "",
      project_id: "",
      subject: "",
      description: "",
      contact_person: "",
      email: "",
      phone: "",
      gst_number: "",
      bill_address: "",
      bill_country: "",
      bill_state: "",
      bill_city: "",
      bill_zipcode: "",
      ship_address: "",
      ship_country: "",
      ship_state: "",
      ship_city: "",
      ship_zipcode: "",
      tds_percentage: "",
      tcs_percentage: "",
      discount_percentage: "",
      discount_amount: "",
      adjust_amount: "",
      customer_note: "",
      terms_conditions: "",
      line_items: "",
    });
  };
  const [subTotal, setSubTotal] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [removedProductList, setRemovedProductList] = useState([]);

  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [pendingItemType, setPendingItemType] = useState(null);

  const [customerValue, setCustomerValue] = useState();
  const [contactValue, setContactValue] = useState();
  const [projectValue, setProjectValue] = useState();
  const [subjectValue, setSubjectValue] = useState();
  const [titleValue, setTitleValue] = useState();
  const [emailValue, setEmailValue] = useState();
  const [phoneValue, setPhoneValue] = useState();
  const [gstValue, setGstValue] = useState();
  const [descriptionValue, setDescriptionValue] = useState();
  const [termsValue, setTermsValue] = useState();
  const [notesValue, setNotesValue] = useState();
  const [salesValue, setSalesValue] = useState();
  const [quotationDate, setQuotationDate] = useState();
  const [quotationDateShow, setQuotationDateShow] = useState();
  const [quotationCode, setQuotationCode] = useState();
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState("%");
  const [tdsType, setTdsType] = useState("TDS"); // default selected
  const [tdsPercentage, setTdsPercentage] = useState("0");
  const [adjustmentValue, setAdjustmentValue] = useState("0");
  const [expiryDate, setExpiryDate] = useState();
  const [initialData, setInitialData] = useState(null);
  const [modifiedFieldsList, setModifiedFieldsList] = useState([]);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [editRemarks, setEditRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");

  const [currencyOption, setCurrencyOption] = useState(null);
  const [exchangeRate, setExchangeRate] = useState("0");

  const handleCurrencyDropdownChange = (option) => {
    setCurrencyOption(option);
  };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Symbol of the selected currency; falls back to ₹ when none is selected.
  const currencySymbol = currencyOption?.symbol
    ? decodeHtmlEntity(currencyOption.symbol)
    : "₹";

  // Organization's own currency — the Conversion Rate field is only shown
  // when the selected currency differs from this. Name/symbol are used to
  // show the "Total in {org currency}" row below the Total.
  const [orgCurrencyId, setOrgCurrencyId] = useState(null);
  const [orgCurrencyName, setOrgCurrencyName] = useState("");
  const [orgCurrencySymbol, setOrgCurrencySymbol] = useState("");
  const showConversionRate =
    orgCurrencyId != null &&
    currencyOption?.value != null &&
    String(orgCurrencyId) !== String(currencyOption.value);

  useEffect(() => {
    const fetchOrgCurrency = async () => {
      try {
        const response = await getOrgCurrencyApi();
        if (response?.success) {
          setOrgCurrencyId(response.data?.org_currency_id ?? null);
          setOrgCurrencyName(response.data?.org_currency_name ?? "");
          setOrgCurrencySymbol(decodeHtmlEntity(response.data?.org_currency_symbol) || "");
        }
      } catch (err) {
        // Non-critical — Conversion Rate simply stays hidden if this fails.
      }
    };
    fetchOrgCurrency();
  }, []);

  const [shipAddress, setShipAddress] = useState("");
  const [shipCountryOption, setShipCountryOption] = useState(null);
  const [shipStateOption, setShipStateOption] = useState(null);
  const [shipCityOption, setShipCityOption] = useState(null);
  const [shipZipCode, setShipZipCode] = useState("");

  const [billAddress, setBillAddress] = useState("");
  const [billCountryOption, setBillCountryOption] = useState(null);
  const [billStateOption, setBillStateOption] = useState(null);
  const [billCityOption, setBillCityOption] = useState(null);
  const [billZipCode, setBillZipCode] = useState("");

  const [discountTotal, setDiscountTotal] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [tdsCalcValue, setTdsCalcValue] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentQuotationId, setCurrentQuotationId] = useState(null);
  const [attachmentUploadError, setAttachmentUploadError] = useState(null);
  const [attachmentUploadInProgress, setAttachmentUploadInProgress] = useState(false);
  const [attachmentDeleteModal, setAttachmentDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFilesAdd = (validFiles, invalidFiles) => {
    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      console.log(`✓ ${validFiles.length} file(s) uploaded successfully`);
    }

    if (invalidFiles.length > 0) {
      console.warn(`⚠ File(s) too large or invalid: ${invalidFiles.join(", ")}. Max size is 5MB.`);
    }
  };

  const handleFileRemove = (index) => {
    const fileToRemove = uploadedFiles[index];

    // If it's an existing attachment from server, show delete modal
    if (fileToRemove?.isExisting && fileToRemove?.id) {
      setAttachmentToDelete({ id: fileToRemove.id, name: fileToRemove.name, index });
      setAttachmentDeleteModal(true);
    } else {
      // New file (not yet saved), just remove from UI
      setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete) return;

    try {
      setIsDeleting(true);
      const response = await removeQuotationAttachmentApi(attachmentToDelete.id);

      if (response?.data?.success) {
        // Remove from UI
        setUploadedFiles((prev) => prev.filter((_, i) => i !== attachmentToDelete.index));
        console.log(`✓ Attachment ${attachmentToDelete.id} deleted successfully`);
        setAttachmentDeleteModal(false);
        setAttachmentToDelete(null);
      } else {
        console.error("⚠ Delete failed:", response?.data);
        alert(`Failed to delete attachment: ${response?.data?.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("⚠ Error deleting attachment:", error);
      alert(`Error deleting attachment: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteAttachment = () => {
    setAttachmentDeleteModal(false);
    setAttachmentToDelete(null);
  };

  const handleCustomerDropdownChange = (option) => {
    setCustomerValue(option);
    // updateStateTaxFlags(option.value);
    getCustomDet(option.value);
  };
  const copyBillingAddress = () => {
    setBillAddress(shipAddress);
    setBillCountryOption(shipCountryOption);
    setBillStateOption(shipStateOption);
    setBillCityOption(shipCityOption);
    setBillZipCode(shipZipCode);
  };

  const handleProjectDropdownChange = (option) => {
    setProjectValue(option);
  };
  const handleSalesDropdownChange = (option) => {
    setSalesValue(option);
  };
  const handleCityDropdownChange = (option) => {
    setShipCityOption(option);
    setShipStateOption({ label: option.state_name, value: option.state_id });
    setShipCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
    updateStateTaxFlags(option.state_id);
  };

  const handleStateDropdownChange = (option) => {
    setShipStateOption(option);
    updateStateTaxFlags(option.value);
    setShipCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
    setShipCityOption(null);
  };
  // Break a row into its calculation parts. Per-item discount (equal split)
  // is removed before tax is applied, matching updateProductTotal.
  const getItemBreakdown = (row) => {
    const unitPrice = Number(row.unit_without_tax) || 0;
    const quantity = Number(row.quantity) || 0;
    const cgst = Number(row.cgst_percentage) || 0;
    const sgst = Number(row.sgst_percentage) || 0;
    const igst = Number(row.igst_percentage) || 0;

    const baseAmount = unitPrice * quantity;

    const count = productList.length || 1;
    const itemDiscount =
      Number(discountTotal) > 0 ? Number(discountTotal) / count : 0;
    const taxableAmount = Math.max(baseAmount - itemDiscount, 0);

    const cgstAmount = taxableAmount * (cgst / 100);
    const sgstAmount = taxableAmount * (sgst / 100);
    const igstAmount = taxableAmount * (igst / 100);
    const taxAmount = cgstAmount + sgstAmount + igstAmount;

    return {
      baseAmount,
      itemDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      total: taxableAmount + taxAmount,
    };
  };

  const calculateTotalAmount = (row) => getItemBreakdown(row).total.toFixed(2);

  const updateProductTotal = (index) => {
    setProductList((prev) => {
      // Per-item discount = total discount split equally across all items.
      // Tax is charged on the amount AFTER this discount is removed.
      const count = prev.length || 1;
      const itemDiscount =
        Number(discountTotal) > 0 ? Number(discountTotal) / count : 0;

      const updated = prev.map((p, i) => {
        // if (i !== index) return p;

        const qty = Number(p.quantity) || 0;
        const unit = Number(p.unit_without_tax) || 0;

        const cgst = Number(p.cgst_percentage) || 0;
        const sgst = Number(p.sgst_percentage) || 0;
        const igst = Number(p.igst_percentage) || 0;

        const baseAmount = qty * unit;
        // Base on which tax is applied = amount minus this item's discount.
        const taxableAmount = Math.max(baseAmount - itemDiscount, 0);

        const cgstAmount = taxableAmount * (cgst / 100);
        const sgstAmount = taxableAmount * (sgst / 100);
        const igstAmount = taxableAmount * (igst / 100);

        // Item total = discounted base + tax on the discounted base.
        const totalAmount =
          taxableAmount + cgstAmount + sgstAmount + igstAmount;

        return {
          ...p,
          total_amount: Number(totalAmount.toFixed(2)),
        };
      });

      // Subtotal stays pre-tax, pre-discount (qty × unit price).
      const newSubtotal = updated.reduce((sum, item) => sum + (Number(item.quantity * item.unit_without_tax) || 0), 0);

      setSubTotal(newSubtotal);

      return updated;
    });
  };

  const discountCalculate = useCallback(
    (val) => {
      let discountAmount = 0;

      // ⛓ Percentage discount
      if (discountType !== "Rs.") {
        discountAmount = subTotal * (Number(val) / 100);
      } else {
        discountAmount = Number(val) || 0;
      }

      setDiscountTotal(discountAmount); // KEEP DECIMALS (no round)
    },
    [discountType, subTotal],
  );

  useEffect(() => {
    if (discountValue !== "") {
      discountCalculate(discountValue);
    }
  }, [subTotal, discountType, discountValue, discountCalculate]);

  const calculateFinalAmount = useCallback(() => {
    const sub = Number(subTotal || 0);
    const discount = Number(discountTotal || 0);
    const adjustment = Number(adjustmentValue || 0);
    const percentage = Number(tdsPercentage || 0);

    // Per-item discount (equal split) — the base is discounted before tax.
    const count = productList.length || 1;
    const itemDiscount = discount > 0 ? discount / count : 0;

    // Total GST across all items = tax-inclusive item total minus the
    // discounted base (qty × unit price − per-item discount). This is the pure
    // tax charged on the post-discount amount.
    const totalItemTax = productList.reduce((sum, item) => {
      const taxInclusive = Number(item.total_amount) || 0;
      const baseAmount = Number(item.quantity) * Number(item.unit_without_tax) || 0;
      const taxableAmount = Math.max(baseAmount - itemDiscount, 0);
      return sum + (taxInclusive - taxableAmount);
    }, 0);
    setTotalTax(totalItemTax);

    // 1️⃣ After Discount
    const afterDiscount = sub - discount;

    // 2️⃣ Total = after-discount amount + item GST + adjustment (no TDS/TCS).
    const finalAmount = afterDiscount + totalItemTax + adjustment;

    // 3️⃣ TDS/TCS is shown separately below the Total — calculated from the
    // without-tax (after-discount, pre-GST) amount, but NOT included back
    // into the Total.
    const tdsAmount = percentage > 0 ? afterDiscount * (percentage / 100) : 0;

    // Save values
    setTdsCalcValue(tdsAmount);
    setFinalTotal(finalAmount);
  }, [subTotal, discountTotal, adjustmentValue, tdsPercentage, productList]);

  // Show the per-item Discount column only when a discount value is entered.
  const hasDiscount =
    discountValue !== "" &&
    discountValue !== null &&
    Number(discountValue) > 0 &&
    Number(discountTotal) > 0;

  // Total discount split equally across all line items.
  const perItemDiscount =
    hasDiscount && productList.length > 0
      ? Number(discountTotal) / productList.length
      : 0;

  // When the discount amount or item count changes, recompute each item's tax
  // (tax is charged on the post-discount amount).
  useEffect(() => {
    updateProductTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountTotal, productList.length]);

  const requestItemTypeChange = (nextType) => {
    if (nextType === itemType) return;
    setPendingItemType(nextType);
    setShowSwitchModal(true);
  };

  const confirmItemTypeChange = () => {
    if (!pendingItemType) return;
    setItemType(pendingItemType);
    setProductList([]);
    setRemovedProductList([]);
    setPendingItemType(null);
    setShowSwitchModal(false);
  };

  const cancelItemTypeChange = () => {
    setPendingItemType(null);
    setShowSwitchModal(false);
  };

  useEffect(() => {
    calculateFinalAmount();
  }, [calculateFinalAmount]);

  const handleCountryDropdownChange = (option) => {
    setShipCountryOption(option);
    setShipCityOption(null);
    setShipStateOption(null);
    updateStateTaxFlags(0);
  };
  const handleBillCityDropdownChange = (option) => {
    setBillCityOption(option);
    setBillStateOption({ label: option.state_name, value: option.state_id });
    setBillCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
  };

  const handleBillStateDropdownChange = (option) => {
    setBillStateOption(option);
    setBillCountryOption({
      label: option.country_name,
      value: option.country_id,
    });
    setShipCityOption(null);
  };

  const handleBillCountryDropdownChange = (option) => {
    setBillCountryOption(option);
    setBillCityOption(null);
    setBillStateOption(null);
  };
  const [validations, setValidations] = useState({
    customer_id: "",
    item_type: "",
    title: "",
    valid_date: "",
    sales_person_id: "",

    quotation_date: "",
    project_id: "",
    subject: "",
    description: "",
    contact_person: "",
    email: "",
    phone: "",
    gst_number: "",
    bill_address: "",
    bill_country: "",
    bill_state: "",
    bill_city: "",
    bill_zipcode: "",
    ship_address: "",
    ship_country: "",
    ship_state: "",
    ship_city: "",
    ship_zipcode: "",
    tds_percentage: "",
    tcs_percentage: "",
    discount_percentage: "",
    discount_amount: "",
    adjust_amount: "",
    customer_note: "",
    terms_conditions: "",
    line_items: "",
  });

  // Helper to get nested validation errors for line items
  const getErrorMsg = (index, field) => {
    return validations[`line_items.${index}.${field}`] || "";
  };

  //APi

  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);
  const updateStateTaxFlags = async (stateId) => {
    await applyStateTaxFlags(stateId, {
      setIsIgst,
      setIsCgst,
      setIsSgst,
    });
  };
  const getCustomDet = async (id, forType) => {
    try {
      let response;
      if ((forType ?? typeValue) === "Customer") {
        response = await getCustomerDetailsApi(id);
      } else {
        response = await getLeadDetailsApi(id);
      }

      if (response.data.success === true) {
        let cust = response.data.customer;
        setEmailValue(cust.email ?? "");
        setPhoneValue(cust.phone ?? "");
        setGstValue(cust.gstin ?? "");
        setContactValue(cust.contact_person ?? null);
        setSalesValue(cust.sales_person ?? null);
        setShipAddress(cust.ship_address ?? "");
        setShipCountryOption(cust.ship_country ?? null);
        setShipStateOption(cust.ship_state ?? null);
        setShipCityOption(cust.ship_city ?? null);
        setShipZipCode(cust.ship_zipcode ?? "");
        setBillAddress(cust.bill_address ?? "");
        setBillCountryOption(cust.bill_country ?? null);
        setBillStateOption(cust.bill_state ?? null);
        setBillCityOption(cust.bill_city ?? null);
        setBillZipCode(cust.bill_zipcode ?? "");
        updateStateTaxFlags(cust.ship_state.value ?? "");
        // Flow the account's default discount % into this quotation.
        if ((forType ?? typeValue) === "Customer" && cust.discount_percentage) {
          setDiscountType("%");
          setDiscountValue(cust.discount_percentage);
        }
      }
    } catch (e) {}
  };

  const [loading, setLoading] = useState(false);

  const getQuotation = useCallback(async () => {
    try {
      // 🔒 VAPT Security: Validate ID before API call
      if (!isValidId(id)) {
        navigate("/quotation/list");
        return;
      }

      let response = await getQuotationDetailsApi(id);
      if (response.success === true) {
        let q = response.data;
        // 🌟 CUSTOMER / LEAD
        if (q.type === "Lead") {
          setType(q.type);
          setCustomerValue(q.lead ?? q.customer ?? null);
        } else {
          setType(q.type ?? "Customer");
          setCustomerValue(q.customer ?? null);
        }
        // 🔒 VAPT: Sanitize text inputs

        setItemType(sanitizeInput(q.item_type ?? ""));
        setTitleValue(sanitizeInput(q.title ?? ""));
        setQuotationCode(sanitizeInput(q.quotation_id ?? ""));
        updateStateTaxFlags(q.ship_state?.value);
        // 🌟 DATES
        setExpiryDate(q.valid_date ?? "");
        setQuotationDate(q.quotation_date ?? "");

        // 🌟 CURRENCY
        setExchangeRate(sanitizeNumericInput(q.exchange_rate ?? "0", MAX_NUMERIC_VALUE));
        if (q.currency && typeof q.currency === "object") {
          setCurrencyOption({
            ...q.currency,
            value: q.currency.value ?? q.currency.currency_id ?? q.currency.id,
            label: q.currency.label ?? q.currency.currency_name ?? q.currency.name,
            symbol: q.currency.symbol ?? q.currency.currency_symbol ?? q.currency_symbol,
          });
        } else if (q.currency_id && q.currency_name) {
          setCurrencyOption({
            value: q.currency_id,
            label: q.currency_name,
            symbol: q.currency_symbol,
          });
        }
        if (q.organization_currency_id != null) {
          setOrgCurrencyId(q.organization_currency_id);
        }
        if (q.org_currency_name) {
          setOrgCurrencyName(q.org_currency_name);
        }
        if (q.org_currency_symbol) {
          setOrgCurrencySymbol(decodeHtmlEntity(q.org_currency_symbol) || "");
        }

        // 🌟 SALES PERSON
        setSalesValue(q.sales_person ?? null);

        // 🌟 PROJECT
        setProjectValue(q.project ?? null);

        // 🌟 SUBJECT / DESCRIPTION
        setSubjectValue(sanitizeInput(q.subject ?? ""));
        setDescriptionValue(sanitizeInput(q.description ?? ""));

        // 🌟 CONTACT PERSON
        setContactValue(q.contact_person ?? null);

        // 🔒 VAPT: Sanitize email, phone, and text inputs
        setEmailValue(sanitizeEmail(q.email ?? ""));
        setPhoneValue(sanitizePhoneNumber(q.phone ?? ""));
        setGstValue(sanitizeInput(q.gst_number ?? ""));

        // 🔒 VAPT: Sanitize billing address
        setBillAddress(sanitizeInput(q.bill_address ?? ""));
        setBillCountryOption(q.bill_country);
        setBillStateOption(q.bill_state);
        setBillCityOption(q.bill_city);
        setBillZipCode(sanitizeInput(q.bill_zipcode ?? ""));

        // 🔒 VAPT: Sanitize shipping address
        setShipAddress(sanitizeInput(q.ship_address ?? ""));
        setShipCountryOption(q.ship_country);
        setShipStateOption(q.ship_state);
        setShipCityOption(q.ship_city);
        setShipZipCode(sanitizeInput(q.ship_zipcode ?? ""));

        // 🔒 VAPT: Sanitize notes and terms
        setNotesValue(sanitizeInput(q.customer_note ?? ""));
        setTermsValue(sanitizeInput(q.terms_conditions ?? ""));

        // 🔒 VAPT: Validate and sanitize discount values
        const discountPercentage = sanitizeNumericInput(q.discount_percentage, 100);
        const discountAmount = sanitizeNumericInput(q.discount_amount, MAX_NUMERIC_VALUE);
        const resolvedDiscountType = Number(discountAmount) > 0 && Number(discountPercentage) <= 0 ? "Rs." : "%";
        const resolvedDiscountVal = Number(discountPercentage) > 0 ? discountPercentage : (Number(discountAmount) > 0 ? discountAmount : 0);
        setDiscountType(resolvedDiscountType);
        setDiscountValue(resolvedDiscountVal);

        // 🔒 VAPT: Validate and sanitize TDS/TCS values
        const tcsPercentage = sanitizeNumericInput(q.tcs_percentage, 100);
        const tdsPercentage = sanitizeNumericInput(q.tds_percentage, 100);
        const resolvedTdsType = Number(tcsPercentage) > 0 ? "TCS" : "TDS";
        const resolvedTdsPercentage = Number(tcsPercentage) > 0 ? tcsPercentage : (Number(tdsPercentage) > 0 ? tdsPercentage : 0);
        setTdsType(resolvedTdsType);
        setTdsPercentage(resolvedTdsPercentage);

        // 🔒 VAPT: Validate adjustment value
        setAdjustmentValue(sanitizeNumericInput(q.adjust_amount ?? 0, MAX_NUMERIC_VALUE));

        // 🔒 VAPT: Sanitize line items with numeric validation
        const editDiscountAmount = sanitizeNumericInput(q.discount_amount, MAX_NUMERIC_VALUE);
        const editDiscountPercentage = sanitizeNumericInput(q.discount_percentage, 100);
        const editSubTotalForTax = Number(sanitizeNumericInput(q.sub_total, MAX_NUMERIC_VALUE)) || 0;
        const editDiscountTotal =
          Number(editDiscountPercentage) > 0
            ? editSubTotalForTax * (Number(editDiscountPercentage) / 100)
            : Number(editDiscountAmount) || 0;
        const editLineItems = q.line_items ?? [];
        const editPerItemDiscount =
          editDiscountTotal > 0 && editLineItems.length > 0
            ? editDiscountTotal / editLineItems.length
            : 0;

        setProductList(
          editLineItems.map((item) => {
            const quantity = Number(sanitizeNumericInput(item.quantity, MAX_NUMERIC_VALUE));
            const unitWithoutTax = Number(sanitizeNumericInput(item.unit_price, MAX_NUMERIC_VALUE));
            const cgstPct = Number(sanitizeNumericInput(item.cgst_percentage, 100)) || 0;
            const sgstPct = Number(sanitizeNumericInput(item.sgst_percentage, 100)) || 0;
            const igstPct = Number(sanitizeNumericInput(item.igst_percentage, 100)) || 0;

            // Same discount-then-tax formula as updateProductTotal, so
            // total_amount is correct immediately on edit-load (no need to
            // wait for the recompute effect to backfill it).
            const baseAmount = quantity * unitWithoutTax;
            const taxableAmount = Math.max(baseAmount - editPerItemDiscount, 0);
            const taxAmount = taxableAmount * ((cgstPct + sgstPct + igstPct) / 100);

            return {
              id: isValidId(item.id) ? item.id : "",
              product: q.item_type === "Product" ? item.product : null,
              service: q.item_type === "Service" && item.service_name != null && item.service_name !== "" ? { label: item.service_name, value: item.service_name } : null,
              service_name: item.service_name ?? "",
              hsnCode: sanitizeInput(item.hsn_no ?? item.product?.hsn ?? item.product?.hsn_no ?? ""),
              quantity,
              unit: item.uom ?? null,
              unit_without_tax: sanitizeNumericInput(item.unit_price, MAX_NUMERIC_VALUE),
              cgst_percentage: sanitizeNumericInput(item.cgst_percentage, 100),
              sgst_percentage: sanitizeNumericInput(item.sgst_percentage, 100),
              igst_percentage: sanitizeNumericInput(item.igst_percentage, 100),
              total_amount: Number((taxableAmount + taxAmount).toFixed(2)),
            };
          }),
        );

        // 🔒 VAPT: Validate totals
        setSubTotal(Number(sanitizeNumericInput(q.sub_total, MAX_NUMERIC_VALUE)));
        setFinalTotal(Number(sanitizeNumericInput(q.grand_total, MAX_NUMERIC_VALUE)));
        // totalTax/finalTotal are recomputed by the productList effect
        // (calculateFinalAmount) once total_amount above is in state.

        // Snapshot initial data for modification tracking
        setInitialData({
          titleValue: sanitizeInput(q.title ?? ""),
          expiryDate: q.valid_date ?? "",
          discountValue: Number(resolvedDiscountVal) || 0,
          discountType: resolvedDiscountType,
          tdsType: resolvedTdsType,
          tdsPercentage: Number(resolvedTdsPercentage) || 0,
          adjustmentValue: Number(sanitizeNumericInput(q.adjust_amount ?? 0, MAX_NUMERIC_VALUE)) || 0,
          notesValue: sanitizeInput(q.customer_note ?? ""),
          termsValue: sanitizeInput(q.terms_conditions ?? ""),
          subjectValue: sanitizeInput(q.subject ?? ""),
          descriptionValue: sanitizeInput(q.description ?? ""),
          salesValueId: q.sales_person?.value ?? q.sales_person?.id ?? q.sales_person_id ?? null,
          salesPersonLabel: q.sales_person?.label ?? q.sales_person?.name ?? q.sales_person_name ?? "",
          projectValueId: q.project?.value ?? q.project?.id ?? q.project_id ?? null,
          projectLabel: q.project?.label ?? q.project?.name ?? q.project_name ?? "",
          contactValue: sanitizeInput(q.contact_person ?? ""),
          emailValue: sanitizeEmail(q.email ?? ""),
          phoneValue: sanitizePhoneNumber(q.phone ?? ""),
          gstValue: sanitizeInput(q.gst_number ?? ""),
          billAddress: sanitizeInput(q.bill_address ?? ""),
          shipAddress: sanitizeInput(q.ship_address ?? ""),
          productList: (q.line_items ?? []).map((item) => ({
            quantity: Number(sanitizeNumericInput(item.quantity, MAX_NUMERIC_VALUE)) || 0,
            unit_without_tax: Number(sanitizeNumericInput(item.unit_price, MAX_NUMERIC_VALUE)) || 0,
            product_id: item.product && isValidId(item.product.value) ? item.product.value : (item.product_id ?? 0),
            service_name: item.service_name ?? "",
          })),
        });
      }
    } catch (error) {
      // Error handled silently
    }
  }, [id, navigate, isValidId]);

  const fetchExistingAttachments = useCallback(async () => {
    try {
      // 🔒 VAPT Security: Validate ID before API call
      if (!isValidId(id)) {
        return;
      }

      const response = await getQuotationAttachmentsApi(id);
      if (response?.data?.success === true) {
        const attachmentList = response.data.attachments ?? [];

        // 🔒 VAPT: Sanitize attachment data and convert to file format
        const existingAttachments = attachmentList.map((att) => ({
          id: att.id,
          name: sanitizeInput(att.file_name ?? ""),
          size: parseInt(att.file_size ?? "0"),
          type: "application/octet-stream",
          base64: att.file_path ?? "", // Store file_path as base64 for display/download
          isExisting: true, // Mark as existing attachment from server
          file_path: att.file_path ?? "",
          first_name: sanitizeInput(att.first_name ?? ""),
          date: sanitizeInput(att.date ?? ""),
        }));

        // Merge existing attachments with newly uploaded ones
        setUploadedFiles((prev) => {
          const existingIds = existingAttachments.map((a) => a.id);
          const newFiles = prev.filter((p) => !p.isExisting || !existingIds.includes(p.id));
          return [...existingAttachments, ...newFiles];
        });
      }
    } catch (error) {
      // Error handled silently - attachments are optional
    }
  }, [id, isValidId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "numeric", month: "long", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  useEffect(() => {
    // Set default dates to today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split("T")[0];
    if (id) {
      getQuotation();
      fetchExistingAttachments(); // Fetch attachments when editing
    } else {
      if (moduleDetail != null) {
        setType(callFrom);
        setCustomerValue(moduleDetail);
        if (isValidId(moduleDetail.value)) {
          getCustomDet(moduleDetail.value, callFrom);
        }
      }
      setUploadedFiles([]); // Clear attachments when creating new
    }

    setQuotationDate(today);
    setQuotationDateShow(formatDate(today));
  }, [id, moduleDetail, callFrom, getQuotation, fetchExistingAttachments]);

  const submitQuotation = async (remarksOverride, changesOverride) => {
    try {
      setLoading(true); // Start loader

      const effectiveRemarks = typeof remarksOverride === "string" ? remarksOverride : editRemarks;
      const effectiveChanges = Array.isArray(changesOverride) ? changesOverride : modifiedFieldsList;

      // 🔒 VAPT: Validate and sanitize line items before submission
      const updatedData = productList.map((item) => ({
        id: is_negiotate === true ? "" : isValidId(item.id) ? item.id : "",
        product_id: item.product && isValidId(item.product.value) ? item.product.value : 0,
        service_name: item.service_name ?? "",
        hsn_no: sanitizeInput(item.hsnCode ?? ""),

        quantity: Number(sanitizeNumericInput(item.quantity, MAX_NUMERIC_VALUE)),
        uom_id: item.unit?.value ?? null,
        unit_price: sanitizeNumericInput(item.unit_without_tax, MAX_NUMERIC_VALUE),
        cgst_percentage: sanitizeNumericInput(item.cgst_percentage, 100),
        sgst_percentage: sanitizeNumericInput(item.sgst_percentage, 100),
        igst_percentage: sanitizeNumericInput(item.igst_percentage, 100),
        total_amount: sanitizeNumericInput(item.total_amount, MAX_NUMERIC_VALUE),
      }));
      // 🔒 VAPT: Validate and sanitize all inputs before submission
      const dataBeforeSanitize = {
        type: sanitizeInput(typeValue),
        item_type: itemType,
        quotation_id: is_negiotate === true && isValidId(id) ? id : null,
        is_negotiate: is_negiotate,
        customer_id: typeValue === "Customer" ? (customerValue && isValidId(customerValue.value) ? customerValue.value : "") : "",
        lead_id: typeValue === "Lead" ? (customerValue && isValidId(customerValue.value) ? customerValue.value : "") : "",
        valid_date: expiryDate ?? "",
        remarks: effectiveRemarks ? sanitizeInput(effectiveRemarks.trim()) : "",
        changes_summary: effectiveChanges.length > 0 ? effectiveChanges.map((c) => (typeof c === "string" ? c : c.label || "")).filter(Boolean).join(", ") : "",
        sales_person_id: salesValue && isValidId(salesValue.value) ? salesValue.value : "",
        title: sanitizeInput(titleValue),
        quotation_date: quotationDate ?? "",
        project_id: projectValue && isValidId(projectValue.value) ? projectValue.value : "",
        subject: sanitizeInput(subjectValue ?? ""),
        description: sanitizeInput(descriptionValue ?? ""),
        contact_person: contactValue ? contactValue : "",
        email: sanitizeEmail(emailValue ?? ""),
        phone: sanitizePhoneNumber(phoneValue ?? ""),
        gst_number: sanitizeInput(gstValue ?? ""),
        bill_address: sanitizeInput(billAddress ?? ""),
        billing_country_id: billCountryOption && isValidId(billCountryOption.value) ? billCountryOption.value : "",
        billing_state_id: billStateOption && isValidId(billStateOption.value) ? billStateOption.value : "",
        billing_city_id: billCityOption && isValidId(billCityOption.value) ? billCityOption.value : "",
        billing_zipcode: sanitizeInput(billZipCode ?? ""),
        ship_address: sanitizeInput(shipAddress ?? ""),
        shipping_country_id: shipCountryOption && isValidId(shipCountryOption.value) ? shipCountryOption.value : "",
        shipping_state_id: shipStateOption && isValidId(shipStateOption.value) ? shipStateOption.value : "",
        shipping_city_id: shipCityOption && isValidId(shipCityOption.value) ? shipCityOption.value : "",
        shipping_zipcode: sanitizeInput(shipZipCode ?? ""),
        currency_id: currencyOption && isValidId(currencyOption.value) ? currencyOption.value : "",
        exchange_rate: sanitizeNumericInput(exchangeRate, MAX_NUMERIC_VALUE),
        tds_percentage: sanitizeNumericInput(tdsType === "TDS" ? tdsPercentage : 0, 100),
        tcs_percentage: sanitizeNumericInput(tdsType === "TCS" ? tdsPercentage : 0, 100),
        discount_percentage: sanitizeNumericInput(discountType === "%" ? discountValue : 0, 100),
        discount_amount: sanitizeNumericInput(discountType === "Rs." ? discountValue : 0, MAX_NUMERIC_VALUE),
        adjust_amount: sanitizeNumericInput(adjustmentValue, MAX_NUMERIC_VALUE),
        customer_note: sanitizeInput(notesValue ?? ""),
        terms_conditions: sanitizeInput(termsValue ?? ""),
        line_items: updatedData,
        removed_products: removedProductList,
        /* attachments: uploadedFiles.length, */
      };

      const dataAfterSanitize = {};
      Object.entries(dataBeforeSanitize).forEach(([key, value]) => {
        if (typeof value === "boolean" || typeof value === "number" || Array.isArray(value) || key === "assignee") {
          dataAfterSanitize[key] = value;
        } else {
          dataAfterSanitize[key] = DOMPurify.sanitize(value);
        }
      });

      let response;
      // 🔒 VAPT: Check if retrying attachment upload (currentQuotationId exists)
      const quotIdForUpdate = currentQuotationId || id;

      if (is_negiotate === true) {
        response = await createQuotationApi(dataAfterSanitize);
      } else if (quotIdForUpdate != null && isValidId(quotIdForUpdate)) {
        // 🔒 VAPT: Validate ID before update API call
        // If currentQuotationId exists, it means we're retrying after attachment error
        response = await updateQuotationApi(dataAfterSanitize, quotIdForUpdate);
      } else {
        response = await createQuotationApi(dataAfterSanitize);
      }

      if (response && response.data && response.data.success === true) {
        const quotationId = response.data?.id || id;
        setCurrentQuotationId(quotationId);
        setAttachmentUploadError(null);

        // Upload attachments if present (only new files, not existing ones)
        const newFilesToUpload = uploadedFiles.filter((file) => !file.isExisting);

        if (newFilesToUpload.length > 0 && quotationId) {
          try {
            setAttachmentUploadInProgress(true);
            let failedAttachments = [];
            let successCount = 0;


              try {
                const bulkPayload = newFilesToUpload.map((fileObj) => ({
                  file_name: fileObj.name,
                  file: fileObj.base64,
                }));

              let responseAttachment = await addQuotationAttachmentsBulkApi(quotationId, bulkPayload);
               if (responseAttachment &&  responseAttachment.data.success === true) {
 setAttachmentUploadError("Attachments uploaded successfully.");

              setAttachmentUploadInProgress(true);

               if (fromView && id) {
                navigate(`/quotation/view/${id}`);
              } else {
                navigate("/quotation/list");
              }
               }else{
                 setAttachmentUploadError("Bulk upload failed. Please try again.");

              setAttachmentUploadInProgress(false);
               }
              } catch (bulkError) {
                // If bulk fails, fallback to individual uploads
                console.warn("Bulk upload failed, attempting individual uploads...", bulkError);

                 setAttachmentUploadError("Bulk upload failed. Please try again.");

              setAttachmentUploadInProgress(false);
              }




            if (successCount > 0 || failedAttachments.length < newFilesToUpload.length) {
              setAttachmentUploadInProgress(false);

              // Navigate if at least one succeeded

            }
          } catch (attachmentError) {
            const errorMsg = `⚠ Error uploading attachments. Quotation saved but attachments failed. Please try uploading again.`;
            setAttachmentUploadError(errorMsg);
            console.error("Attachment upload error:", attachmentError);
            setAttachmentUploadInProgress(false);
            return; // Stay on same page
          }
        } else {
          // No attachments to upload, navigate normally
          if (fromView && id) {
            navigate(`/quotation/view/${id}`);
          } else {
            navigate("/quotation/list");
          }
        }
      } else if (response && response.data && response.data.success === false) {
        const validationTemp = {
          item_type: "",
          customer_id: "",
          valid_date: "",
          sales_person_id: "",
          quotation_date: "",
          project_id: "",
          subject: "",
          description: "",
          contact_person: "",
          email: "",
          phone: "",
          gst_number: "",
          bill_address: "",
          bill_country: "",
          bill_state: "",
          bill_city: "",
          bill_zipcode: "",
          ship_address: "",
          ship_country: "",
          ship_state: "",
          ship_city: "",
          ship_zipcode: "",
          tds_percentage: "",
          tcs_percentage: "",
          discount_percentage: "",
          discount_amount: "",
          adjust_amount: "",
          customer_note: "",
          terms_conditions: "",
          line_items: "",
        };

        const errorData = response.data.error || response.data.errors || response.data || {};

        Object.entries(errorData).forEach(([key, value]) => {
          const firstError = Array.isArray(value) && value.length > 0 ? value[0] : typeof value === "string" ? value : "";

          // Populate both fixed fields and dynamic line item fields
          validationTemp[key] = firstError;

          // Mapping for lead_id to show on the same customer/lead field
          if (key === "lead_id") {
            validationTemp["customer_id"] = firstError;
          }

          // Support for address field variants
          const addressMap = {
            billing_country_id: "bill_country",
            billing_state_id: "bill_state",
            billing_city_id: "bill_city",
            billing_zipcode: "bill_zipcode",
            shipping_country_id: "ship_country",
            shipping_state_id: "ship_state",
            shipping_city_id: "ship_city",
            shipping_zipcode: "ship_zipcode",
          };

          if (key in addressMap) {
            validationTemp[addressMap[key]] = firstError;
          }
        });
        setValidations(validationTemp);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false); // Stop loader
    }
  };

  const getModifiedFieldsList = () => {
    if (!initialData) return [];
    const changes = [];

    if (expiryDate && expiryDate !== initialData.expiryDate) {
      changes.push({
        label: "Validity Date",
        from: initialData.expiryDate || "(Empty)",
        to: expiryDate,
      });
    }
    if ((titleValue || "") !== (initialData.titleValue || "")) {
      changes.push({
        label: "Title",
        from: initialData.titleValue || "(Empty)",
        to: titleValue || "(Empty)",
      });
    }
    if ((subjectValue || "") !== (initialData.subjectValue || "")) {
      changes.push({
        label: "Subject",
        from: initialData.subjectValue || "(Empty)",
        to: subjectValue || "(Empty)",
      });
    }
    if ((descriptionValue || "") !== (initialData.descriptionValue || "")) {
      changes.push({
        label: "Project Description",
        from: initialData.descriptionValue || "(Empty)",
        to: descriptionValue || "(Empty)",
      });
    }

    const currDiscount = Number(discountValue || 0);
    const initDiscount = Number(initialData.discountValue || 0);
    if (
      (currDiscount > 0 || initDiscount > 0) &&
      (currDiscount !== initDiscount || discountType !== initialData.discountType)
    ) {
      changes.push({
        label: "Discount",
        from: `${initialData.discountValue} ${initialData.discountType}`,
        to: `${discountValue} ${discountType}`,
      });
    }

    const currAdjustment = Number(adjustmentValue || 0);
    const initAdjustment = Number(initialData.adjustmentValue || 0);
    if (currAdjustment !== initAdjustment) {
      changes.push({
        label: "Adjustment Amount",
        from: `${currencySymbol}${initialData.adjustmentValue || 0}`,
        to: `${currencySymbol}${adjustmentValue || 0}`,
      });
    }

    const currTds = Number(tdsPercentage || 0);
    const initTds = Number(initialData.tdsPercentage || 0);
    if (
      (currTds > 0 || initTds > 0) &&
      (currTds !== initTds || tdsType !== initialData.tdsType)
    ) {
      changes.push({
        label: `${tdsType}`,
        from: `${initialData.tdsType} (${initialData.tdsPercentage || 0}%)`,
        to: `${tdsType} (${tdsPercentage || 0}%)`,
      });
    }

    if ((notesValue || "") !== (initialData.notesValue || "")) {
      changes.push({
        label: "Customer Notes",
        from: initialData.notesValue ? (initialData.notesValue.length > 50 ? initialData.notesValue.substring(0, 50) + "..." : initialData.notesValue) : "(Empty)",
        to: notesValue ? (notesValue.length > 50 ? notesValue.substring(0, 50) + "..." : notesValue) : "(Empty)",
      });
    }
    if ((termsValue || "") !== (initialData.termsValue || "")) {
      changes.push({
        label: "Terms & Conditions",
        from: initialData.termsValue ? (initialData.termsValue.length > 50 ? initialData.termsValue.substring(0, 50) + "..." : initialData.termsValue) : "(Empty)",
        to: termsValue ? (termsValue.length > 50 ? termsValue.substring(0, 50) + "..." : termsValue) : "(Empty)",
      });
    }

    const currentSalesId = salesValue?.value ?? salesValue?.id ?? null;
    if (currentSalesId && currentSalesId !== initialData.salesValueId) {
      changes.push({
        label: "Sales Person",
        from: initialData.salesPersonLabel || "(Empty)",
        to: salesValue?.label || salesValue?.name || "(Empty)",
      });
    }

    const currentProjId = projectValue?.value ?? projectValue?.id ?? null;
    if (currentProjId && currentProjId !== initialData.projectValueId) {
      changes.push({
        label: "Project",
        from: initialData.projectLabel || "(Empty)",
        to: projectValue?.label || projectValue?.name || "(Empty)",
      });
    }

    if ((contactValue || "") !== (initialData.contactValue || "")) {
      changes.push({
        label: "Contact Person",
        from: initialData.contactValue || "(Empty)",
        to: contactValue || "(Empty)",
      });
    }
    if ((emailValue || "") !== (initialData.emailValue || "")) {
      changes.push({
        label: "Email",
        from: initialData.emailValue || "(Empty)",
        to: emailValue || "(Empty)",
      });
    }
    if ((phoneValue || "") !== (initialData.phoneValue || "")) {
      changes.push({
        label: "Phone Number",
        from: initialData.phoneValue || "(Empty)",
        to: phoneValue || "(Empty)",
      });
    }
    if ((gstValue || "") !== (initialData.gstValue || "")) {
      changes.push({
        label: "GST Number",
        from: initialData.gstValue || "(Empty)",
        to: gstValue || "(Empty)",
      });
    }

    if ((billAddress || "") !== (initialData.billAddress || "")) {
      changes.push({
        label: "Billing Address",
        from: initialData.billAddress || "(Empty)",
        to: billAddress || "(Empty)",
      });
    }
    if ((shipAddress || "") !== (initialData.shipAddress || "")) {
      changes.push({
        label: "Shipping Address",
        from: initialData.shipAddress || "(Empty)",
        to: shipAddress || "(Empty)",
      });
    }

    // Line items change detection
    if (
      productList.length !== initialData.productList?.length ||
      removedProductList.length > 0
    ) {
      changes.push({
        label: "Line Items",
        from: `${initialData.productList?.length || 0} item(s)`,
        to: `${productList.length} item(s)`,
      });
    } else {
      const itemsChanged = productList.some((item, idx) => {
        const init = initialData.productList?.[idx];
        if (!init) return true;
        const currentProdId = item.product && isValidId(item.product.value) ? item.product.value : (item.product_id ?? 0);
        return (
          Number(item.quantity || 0) !== Number(init.quantity || 0) ||
          Number(item.unit_without_tax || 0) !== Number(init.unit_without_tax || 0) ||
          currentProdId !== init.product_id ||
          (item.service_name ?? "") !== (init.service_name ?? "")
        );
      });
      if (itemsChanged) {
        changes.push({
          label: "Line Items (Pricing/Qty)",
          from: "Original values",
          to: "Updated values",
        });
      }
    }

    if (uploadedFiles.some((f) => !f.isExisting)) {
      const newFilesCount = uploadedFiles.filter((f) => !f.isExisting).length;
      changes.push({
        label: "Attachments",
        from: `${uploadedFiles.filter((f) => f.isExisting).length} file(s)`,
        to: `+${newFilesCount} new file(s)`,
      });
    }

    return changes;
  };

  const handleSaveClick = () => {
    // If editing an existing quotation, detect changes and prompt mandatory remarks
    if (id && is_negiotate !== true) {
      const changes = getModifiedFieldsList();
      if (changes.length > 0 && !editRemarks.trim()) {
        setModifiedFieldsList(changes);
        setRemarksError("");
        setShowRemarksModal(true);
        return;
      }
    }
    submitQuotation();
  };

  const handleRemarksSubmit = (e) => {
    e?.preventDefault();
    if (!editRemarks || !editRemarks.trim()) {
      setRemarksError("Remarks is mandatory for saving changes.");
      return;
    }
    if (editRemarks.trim().length < 3) {
      setRemarksError("Remarks must be at least 3 characters long.");
      return;
    }
    setShowRemarksModal(false);
    submitQuotation(editRemarks.trim(), modifiedFieldsList);
  };

  return (
    <>
      <div
        style={{
          fontFamily: "'Euclid Circular B', sans-serif",
          padding: "16px",
          backgroundColor: "#F7F8FC",
        }}
      >
        <Row className="align-items-center justify-content-between g-2" style={{ width: "100%" }}>
          <Col xs={12} md="auto">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img src={Back} style={{ width: "40px", cursor: "pointer" }} alt="Back" onClick={() => (fromView && id) ? navigate(`/quotation/view/${id}`) : navigate("/quotation/list")} />
              <div className="text-dkblue fs-20 fw-500">{is_negiotate === true ? "Negotiate Quotation" : id ? "Edit Quotation" : "New quotation"}</div>
            </div>
          </Col>

          <Col xs={12} md="auto">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                justifyContent: "flex-end",
                flexWrap: "nowrap",
              }}
            >
              <div
                className="date-card d-flex align-items-center justify-content-center px-3 py-2"
                style={{
                  height: "36px",
                  borderRadius: "100px",
                  backgroundColor: "#EBEBED",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  width: "max-content",
                }}
              >
                <span className="fs-14 fw-500 text-dkblue" style={{ whiteSpace: "nowrap" }}>{quotationDateShow}</span>
              </div>
              {id && (
                <div
                  className="date-card d-flex align-items-center justify-content-center px-3 py-2"
                  style={{
                    height: "36px",
                    borderRadius: "100px",
                    backgroundColor: "#EBEBED",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    width: "max-content",
                  }}
                >
                  <span className="fs-14 fw-500 text-dkblue" style={{ whiteSpace: "nowrap" }}>{quotationCode}</span>
                </div>
              )}
              <button
                className="fs-16 fw-500 textblk"
                style={{
                  height: "40px",
                  borderRadius: "100px",
                  border: "1px solid #d4dde7",
                  backgroundColor: "#ffffff",
                  padding: "10px 28px",
                  lineHeight: "100%",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onClick={() => (fromView && id) ? navigate(`/quotation/view/${id}`) : navigate("/quotation/list")}
              >
                Cancel
              </button>
              <button
                className="fs-16 fw-500 text-white"
                style={{
                  height: "40px",
                  borderRadius: "100px",
                  backgroundColor: "#3d4af5",
                  padding: "10px 28px",
                  lineHeight: "100%",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onClick={handleSaveClick}
                disabled={loading} // Disable button while loading
              >
                {loading ? <Spinner animation="border" size="sm" style={{ color: "white" }} /> : "Save"}
              </button>
            </div>
          </Col>
        </Row>
        <Row style={{ marginTop: "24px" }}>
          <Col md={6}>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                height: "100%",
              }}
            >
              <div className="fw-600 fs-18 text-dkblue">Quotation details</div>
              <Row className="mt-2">
                <Col sm={12} xl={6}>
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {" "}
                    <div className="fs-13 fw-400 textblk">Type</div>
                    <select
                      value={typeValue}
                      onChange={handleTypeChange}
                      style={{
                        height: "36px",
                        // padding: "10px",
                        paddingLeft: "12px",
                        border: "1px solid #E5E5E5",
                        borderRadius: "8px",
                        background: "#FFFFFF",
                        color: "#1E1E1E",
                        width: "100%",
                        outline: "none",
                        // appearance: "none",
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      <option value="Customer">Customer</option>
                      <option value="Lead">Lead</option>
                    </select>
                    <span
                      style={{
                        fontWeight: "400",
                        fontSize: "12px",
                        color: "#ff4444",
                      }}
                    >
                      {validations.call_type}
                    </span>
                  </div>
                </Col>
                <Col sm={12} xl={6}>
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">
                      {typeValue}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        *
                      </span>
                    </div>
                    <div>
                      <SearchWithAPI onDropdownChange={handleCustomerDropdownChange} apiUrl={apiUrl + (typeValue === "Lead" ? "get_leads" : "get_customers")} placeholder={"Select"} selectedOption={customerValue} />{" "}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.customer_id}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div className="fs-13 fw-400 textblk">Title</div>
                <div>
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    className="fs-14 fw-400"
                    style={{
                      height: "36px",
                      borderRadius: "8px",
                      border: "0.6px solid #d4dde7",
                      backgroundColor: "#ffffff",
                      padding: "16px 12px",
                      width: "100%",

                      color: "#172b4d",
                    }}
                    placeholder="Enter here..."
                  />{" "}
                </div>
                <span
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  {validations.title}
                </span>
              </div>
              <Row className="mt-2">
                <Col sm={12} xl={6}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">
                      Expiry / Valid-till Date
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        *
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col sm={12} md={12} style={{ paddingLeft: "0px" }}>
                          <div style={{ width: "100%" }}>
                            <input
                              value={expiryDate}
                              onChange={(e) => setExpiryDate(e.target.value)}
                              type="date"
                              style={{
                                height: "36px",
                                borderRadius: "10px",
                                border: "0.6px solid #d4dde7",
                                paddingLeft: "12px",
                                paddingRight: "12px",
                                backgroundColor: "#ffffff",
                                width: "100%",
                              }}
                            />
                          </div>{" "}
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.valid_date}
                          </span>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
                <Col sm={12} xl={6}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">
                      Sales Person{" "}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        *
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col sm={12} md={12} style={{ paddingLeft: "0px", height: "36px" }}>
                          <SearchWithAPI onDropdownChange={handleSalesDropdownChange} apiUrl={apiUrl + "get_contact_person"} placeholder={"Select"} selectedOption={salesValue} />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.sales_person_id}
                          </span>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col sm={12} xl={6}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">Currency</div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col sm={12} md={12} style={{ paddingLeft: "0px", height: "36px" }}>
                          <SearchWithAPI onDropdownChange={handleCurrencyDropdownChange} apiUrl={apiUrl + "get_currency"} placeholder={"Select"} selectedOption={currencyOption} />
                          <span
                            style={{
                              color: "#ff4444",
                              fontSize: "12px",
                              fontWeight: "400",
                            }}
                          >
                            {validations.currency_id}
                          </span>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
                {showConversionRate && (
                  <Col sm={12} xl={6}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div className="fs-13 fw-400 textblk">Conversion Rate</div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Row style={{ width: "100%", margin: "0" }}>
                          <Col sm={12} md={12} style={{ paddingLeft: "0px" }}>
                            <div style={{ width: "100%" }}>
                              <input
                                value={exchangeRate}
                                onChange={(e) => setExchangeRate(sanitizeNumericInput(e.target.value))}
                                type="text"
                                placeholder="Enter conversion rate"
                                style={{
                                  height: "36px",
                                  borderRadius: "10px",
                                  border: "0.6px solid #d4dde7",
                                  paddingLeft: "12px",
                                  paddingRight: "12px",
                                  backgroundColor: "#ffffff",
                                  width: "100%",
                                }}
                              />
                            </div>{" "}
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.exchange_rate}
                            </span>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
              {/* <Row className="mt-2">
                <Col sm={12} xl={6}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">
                      Quotation date{" "}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        *
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col sm={12} md={12} style={{ paddingLeft: "0px" }}>
                          <div style={{ width: "100%" }}>
                            {" "}
                            {!quotDateEdit && (
                              <div
                                className="date-card d-flex align-items-center justify-content-between px-3 py-2"
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: "#F8F8F8",
                                  border: "0.6px solid #D4DDE7",
                                  height: "36px",
                                }}
                              >
                                <span className="date-text">
                                  {quotationDate}
                                </span>

                              </div>
                            )}
                            {quotDateEdit && (
                              <input
                                value={quotationDate}
                                onChange={(e) =>
                                  setQuotationDate(e.target.value)
                                }
                                type="date"
                                style={{
                                  height: "36px",
                                  borderRadius: "10px",
                                  border: "0.6px solid #d4dde7",
                                  paddingLeft: "12px",
                                  paddingRight: "12px",
                                  backgroundColor: "#ffffff",
                                  width: "100%",
                                }}
                              />
                            )}{" "}
                            <span
                              style={{
                                color: "#ff4444",
                                fontSize: "12px",
                                fontWeight: "400",
                              }}
                            >
                              {validations.quotation_date}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
                <Col sm={12} xl={6}>
                  {id && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div className="fs-13 fw-400 textblk">
                        Quotation Code{" "}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Row style={{ width: "100%", margin: "0" }}>
                          <Col sm={12} md={12} style={{ paddingLeft: "0px" }}>
                            <div style={{ width: "100%" }}>
                              {" "}
                              <div
                                className="date-card d-flex align-items-center justify-content-between px-3 py-2"
                                style={{
                                  height: "36px",
                                  borderRadius: "8px",
                                  backgroundColor: "#F8F8F8",
                                  border: "0.6px solid #D4DDE7",
                                }}
                              >
                                <span className="date-text">
                                  {quotationCode}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  )}
                </Col>
              </Row> */}
            </div>
          </Col>

          <Col md={6}>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                marginLeft: "12px", // 👈 space between cards
                height: "100%",
              }}
            >
              <div className="fw-600 fs-18 text-dkblue">Project details</div>
              <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div className="fs-13 fw-400 textblk">Project </div>
                <div>
                  <SearchWithAPI onDropdownChange={handleProjectDropdownChange} apiUrl={apiUrl + "get_projects"} placeholder={"Select"} selectedOption={projectValue} />{" "}
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.project_id}
                  </span>
                </div>
              </div>
              <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div className="fs-13 fw-400 textblk">Subject</div>
                <div>
                  <input
                    type="text"
                    value={subjectValue}
                    onChange={(e) => setSubjectValue(e.target.value)}
                    className="fs-14 fw-400"
                    style={{
                      height: "36px",
                      borderRadius: "8px",
                      border: "0.6px solid #d4dde7",
                      backgroundColor: "#ffffff",
                      padding: "16px 12px",
                      width: "100%",

                      color: "#172b4d",
                    }}
                    placeholder="Enter here..."
                  />{" "}
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.subject}
                  </span>
                </div>
              </div>
              <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <div className="fs-13 fw-400 textblk">Description</div>
                <div>
                  <textarea
                    value={descriptionValue}
                    onChange={(e) => setDescriptionValue(e.target.value)}
                    className="fs-14 fw-400"
                    style={{
                      borderRadius: "8px",
                      border: "0.6px solid #d4dde7",
                      backgroundColor: "#ffffff",
                      padding: "16px 12px",
                      width: "100%",

                      color: "#172b4d",
                    }}
                    placeholder="Enter here..."
                  />{" "}
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.description}
                  </span>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: "24px" }}>
          <Col>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                marginLeft: "12px",
                height: "100%",
              }}
            >
              <div className="fw-600 fs-18 text-dkblue mb-3">{typeValue} Information</div>

              {/* 👇 Inner Row for columns */}
              <Row>
                <Col>
                  {" "}
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">
                      Contact Person{" "}
                      {typeValue === "Lead" && (
                        <span
                          style={{
                            color: "#ff4444",
                            fontSize: "12px",
                            fontWeight: "400",
                          }}
                        >
                          *
                        </span>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        value={contactValue || ""}
                        onChange={(e) => setContactValue(e.target.value)}
                        className="fs-14 fw-400"
                        style={{
                          height: "36px",
                          borderRadius: "8px",
                          border: "0.6px solid #d4dde7",
                          backgroundColor: "#ffffff",
                          padding: "16px 12px",
                          width: "100%",

                          color: "#172b4d",
                        }}
                        placeholder="Enter here..."
                      />{" "}
                      {/* <SearchWithAPI
                        onDropdownChange={handleContactDropdownChange}
                        apiUrl={apiUrl + "get_contact_person"}
                        placeholder={"Select"}
                        selectedOption={contactValue}
                      />{" "} */}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.contact_person}
                      </span>
                    </div>
                  </div>
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">
                      Email
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        *
                      </span>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={emailValue || ""}
                        onChange={(e) => setEmailValue(e.target.value)}
                        className="fs-14 fw-400"
                        style={{
                          height: "36px",
                          borderRadius: "8px",
                          border: "0.6px solid #d4dde7",
                          backgroundColor: "#ffffff",
                          padding: "16px 12px",
                          width: "100%",

                          color: "#172b4d",
                        }}
                        placeholder="Enter here..."
                      />
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.email}
                      </span>
                    </div>
                  </div>
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">Phone</div>
                    <div>
                      <input
                        type="number"
                        value={phoneValue || ""}
                        onChange={(e) => setPhoneValue(e.target.value)}
                        className="fs-14 fw-400"
                        style={{
                          height: "36px",
                          borderRadius: "8px",
                          border: "0.6px solid #d4dde7",
                          backgroundColor: "#ffffff",
                          padding: "16px 12px",
                          width: "100%",

                          color: "#172b4d",
                        }}
                        placeholder="Enter here..."
                      />{" "}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.phone}
                      </span>
                    </div>
                  </div>
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div className="fs-13 fw-400 textblk">GST / Tax ID</div>
                    <div>
                      <input
                        type="text"
                        value={gstValue || ""}
                        onChange={(e) => setGstValue(e.target.value)}
                        className="fs-14 fw-400"
                        style={{
                          height: "36px",
                          borderRadius: "8px",
                          border: "0.6px solid #d4dde7",
                          backgroundColor: "#ffffff",
                          padding: "16px 12px",
                          width: "100%",

                          color: "#172b4d",
                        }}
                        placeholder="Enter here..."
                      />{" "}
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.gst_number}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col>
                  {" "}
                  <div
                    style={{
                      backgroundColor: "#FAFAFA",
                      borderRadius: "12px",
                      padding: "16px",
                    }}
                  >
                    <p className="fs-12 fw-600 " style={{ color: "#64748B" }}>
                      SHIPPING ADDRESS{" "}
                    </p>
                    <div className=" d-flex flex-column align-items-start justify-content-start">
                      <div className="fs-13 fw-400 textblk mb-1">Address</div>
                      <div className="w-100">
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="Owner"
                          placeholder="Enter here"
                          rows={3}
                          value={shipAddress}
                          onChange={(e) => {
                            setShipAddress(e.target.value);
                          }}
                        />{" "}
                      </div>
                    </div>

                    <Row className="mt-2">
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">City</div>
                        <div className="w-100">
                          <SearchWithAPI
                            onDropdownChange={handleCityDropdownChange}
                            apiUrl={apiAccUrl + "get_city"}
                            params={{
                              state_id: shipStateOption != null ? shipStateOption.value : "",
                            }}
                            placeholder={"Select"}
                            selectedOption={shipCityOption}
                          />
                        </div>
                      </Col>

                      {/* State */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">State</div>
                        <div className="w-100">
                          <SearchWithAPI
                            onDropdownChange={handleStateDropdownChange}
                            apiUrl={apiAccUrl + "get_state"}
                            params={{
                              country_id: shipCountryOption != null ? shipCountryOption.value : "",
                            }}
                            placeholder={"Select"}
                            selectedOption={shipStateOption}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      {/* City */}
                      {/* Country */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">Country</div>
                        <div className="w-100">
                          <SearchWithAPI onDropdownChange={handleCountryDropdownChange} apiUrl={apiAccUrl + "get_country"} params={{}} placeholder={"Select"} selectedOption={shipCountryOption} />
                        </div>
                      </Col>

                      {/* Zip Code */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">Zip Code</div>
                        <div className="w-100">
                          <input type="text" className="form-control rounded-3 vendor-form-input" id="shipZipCode" placeholder="Enter here" value={shipZipCode} onChange={(e) => setShipZipCode(e.target.value)} />
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
                <Col>
                  {" "}
                  <div
                    style={{
                      backgroundColor: "#FAFAFA",
                      borderRadius: "12px",
                      padding: "16px",
                    }}
                  >
                    <p className="fs-12 fw-600 " style={{ color: "#64748B" }}>
                      BILLING ADDRESS{" "}
                      <span onClick={copyBillingAddress} className="fs-12 fw-600 textblue" style={{ cursor: "pointer" }}>
                        (Copy as shipping Address)
                      </span>
                    </p>
                    <div className=" d-flex flex-column align-items-start justify-content-start">
                      <div className="fs-13 fw-400 textblk mb-1">Address</div>
                      <div className="w-100">
                        <textarea
                          type="text"
                          className="form-control rounded-3 vendor-form-input"
                          id="Owner"
                          placeholder="Enter here"
                          rows={3}
                          value={billAddress}
                          onChange={(e) => {
                            setBillAddress(e.target.value);
                          }}
                        />{" "}
                      </div>
                    </div>

                    <Row className="mt-2">
                      {/* Country */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">City</div>
                        <div className="w-100">
                          <SearchWithAPI
                            onDropdownChange={handleBillCityDropdownChange}
                            apiUrl={apiAccUrl + "get_city"}
                            params={{
                              state_id: billStateOption != null ? billStateOption.value : "",
                            }}
                            placeholder={"Select"}
                            selectedOption={billCityOption}
                          />
                        </div>
                      </Col>

                      {/* State */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">State</div>
                        <div className="w-100">
                          <SearchWithAPI
                            onDropdownChange={handleBillStateDropdownChange}
                            apiUrl={apiAccUrl + "get_state"}
                            params={{
                              country_id: billCountryOption != null ? billCountryOption.value : "",
                            }}
                            placeholder={"Select"}
                            selectedOption={billStateOption}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      {/* City */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">Country</div>
                        <div className="w-100">
                          <SearchWithAPI onDropdownChange={handleBillCountryDropdownChange} apiUrl={apiAccUrl + "get_country"} params={{}} placeholder={"Select"} selectedOption={billCountryOption} />
                        </div>
                      </Col>

                      {/* Zip Code */}
                      <Col md={6} className="d-flex flex-column align-items-start justify-content-start mb-2">
                        <div className="fs-13 fw-400 textblk mb-1">Zip Code</div>
                        <div className="w-100">
                          <input type="text" className="form-control rounded-3 vendor-form-input" id="shipZipCode" placeholder="Enter here" value={billZipCode} onChange={(e) => setBillZipCode(e.target.value)} />
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <Row style={{ marginTop: "24px" }}>
          <Col>
            {" "}
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                marginLeft: "12px",
                height: "100%",
              }}
            >
              {" "}
              <div className="products-header-row">
                {/* <div className="fw-600 fs-18 text-dkblue">
                  Products
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    *
                  </span>
                </div> */}
                <div className="item-type-toggle">
                  <span className="item-type-label">
                    Items :
                    <span
                      style={{
                        color: "#ff4444",
                        fontSize: "12px",
                        fontWeight: "400",
                      }}
                    >
                      *
                    </span>
                  </span>
                  <div className="item-type-group">
                    <label className={`item-type-option ${itemType === "Product" ? "active" : ""}`}>
                      <input type="radio" name="itemType" value="Product" checked={itemType === "Product"} onChange={(e) => requestItemTypeChange(e.target.value)} />
                      <span className="item-type-text">Product</span>
                    </label>
                    <label className={`item-type-option ${itemType === "Service" ? "active" : ""}`}>
                      <input type="radio" name="itemType" value="Service" checked={itemType === "Service"} onChange={(e) => requestItemTypeChange(e.target.value)} />
                      <span className="item-type-text">Service</span>
                    </label>
                  </div>
                </div>
              </div>
              <span
                style={{
                  color: "#ff4444",
                  fontSize: "12px",
                  fontWeight: "400",
                }}
              >
                {validations.line_items}
              </span>
              <div className="row">
                <div className="product-table-container">
                  {productList.length > 0 ? (
                    <div
                      className="bg-white rounded-4 mt-3"
                      style={{
                        minWidth: "900px",
                      }}
                    >
                      {/* Header Row */}
                      <div
                        style={{
                          backgroundColor: "#F7F8FC",
                          display: "grid",
                          gridTemplateColumns: `1.5fr 0.9fr 1.0fr 0.8fr 0.8fr 0.8fr ${hasDiscount ? "0.8fr" : ""} ${isCgst ? "0.6fr" : ""} ${isSgst ? "0.6fr" : ""} ${isIgst ? "0.6fr" : ""} 1.2fr 50px`,
                          padding: "10px 16px",
                          gap: "10px",
                          alignItems: "center",
                          borderBottom: "1px solid #EBEBEB",
                          minWidth: "fit-content",
                        }}
                      >
                        <div className="fs-11 fw-600 textGRE">{itemType === "Product" ? "PRODUCT" : "SERVICE"} NAME</div>
                        {/* <div className="fs-11 fw-600 textGRE">PRODUCT ID</div> */}
                        <div className="fs-11 fw-600 textGRE">HSN CODE</div>
                        <div className="fs-11 fw-600 textGRE">UNIT</div>

                        <div className="fs-11 fw-600 textGRE">QUANTITY</div>
                        <div className="fs-11 fw-600 textGRE">UNIT PRICE</div>
                        <div className="fs-11 fw-600 textGRE">AMOUNT</div>
                        {hasDiscount && <div className="fs-11 fw-600 textGRE">DISCOUNT</div>}
                        {isCgst && <div className="fs-11 fw-600 textGRE">CGST %</div>}
                        {isSgst && <div className="fs-11 fw-600 textGRE">SGST %</div>}
                        {isIgst && <div className="fs-11 fw-600 textGRE">IGST %</div>}
                        <div className="fs-11 fw-600 textGRE">TOTAL AMOUNT</div>

                        <div className="fs-11 fw-600 textGRE"></div>
                      </div>

                      {productList.map((row, index) => {
                        return (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #EBEBEB",
                              paddingBottom: "6px",
                            }}
                          >
                            <div
                              key={index}
                              style={{
                                display: "grid",
                                gridTemplateColumns: `1.5fr 0.9fr 1.0fr 0.8fr 0.8fr 0.8fr ${hasDiscount ? "0.8fr" : ""} ${isCgst ? "0.6fr" : ""} ${isSgst ? "0.6fr" : ""} ${isIgst ? "0.6fr" : ""} 1.2fr 50px`,
                                padding: "10px 16px",
                                gap: "10px",
                                alignItems: "center",
                              }}
                            >
                              {/* Exception/Proceed Column */}

                              {/* Product Name Dropdown */}
                              <div style={{ minWidth: 0 }}>
                                {itemType === "Product" && (
                                  <CommonDropdown
                                    apiUrl={`${apiUrl}get_products?term=`}
                                    placeholder="Select"
                                    value={row.product}
                                    onSelect={(item) => {
                                      const productTemp = [...productList];
                                      productTemp[index].product = item;
                                      // Prefill editable HSN from the product.
                                      productTemp[index].hsnCode = sanitizeInput(
                                        item?.hsn_no ?? item?.hsn ?? "",
                                      );
                                      // Prefill unit price and tax % from the
                                      // item master. Tax is split across
                                      // CGST+SGST (intra-state) or IGST
                                      // (inter-state) per the quotation's
                                      // current state-tax flags.
                                      const itemTaxPct =
                                        Number(sanitizeNumericInput(item?.tax_percentage, 100)) || 0;
                                      productTemp[index].unit_without_tax = sanitizeNumericInput(
                                        item?.unit_price,
                                        MAX_NUMERIC_VALUE,
                                      );
                                      // Prefill UOM from the item master.
                                      if (item?.uom?.value) {
                                        productTemp[index].unit = item.uom;
                                      }
                                      if (isIgst) {
                                        productTemp[index].igst_percentage = itemTaxPct;
                                        productTemp[index].cgst_percentage = 0;
                                        productTemp[index].sgst_percentage = 0;
                                      } else {
                                        productTemp[index].cgst_percentage = itemTaxPct / 2;
                                        productTemp[index].sgst_percentage = itemTaxPct / 2;
                                        productTemp[index].igst_percentage = 0;
                                      }
                                      setProductList(productTemp);
                                      updateProductTotal(index);
                                    }}
                                  />
                                )}
                                {itemType === "Service" && (
                                  <SearchWithFreeTextAPI
                                    onDropdownChange={(option) => {
                                      const productTemp = [...productList];
                                      /* productTemp[index].service = option; */
                                      productTemp[index].service_name = option.value;
                                       productTemp[index].hsnCode = sanitizeInput(
                                        option?.hsn_no ?? option?.hsn ?? "",
                                      );
                                      setProductList(productTemp);
                                    }}
                                    onChangeSearch={(term) => {
                                      const productTemp = [...productList];
                                      /* productTemp[index].service = option; */
                                      productTemp[index].service_name = term;

                                      setProductList(productTemp);
                                    }}
                                    apiUrl={apiUrl + `get_services`}
                                    params={{}}
                                    searchLimit={0}
                                    placeholder={"Search searvice..."}
                                    selectedValue={row.service_name}
                                  />
                                )}
                                {/* {itemType === "Service" && (
                                <CommonDropdown
                                  apiUrl={`${apiUrl}get_services?term=`}
                                  placeholder="Select"
                                  value={row.service}
                                  onSelect={(item) => {
                                    const productTemp = [...productList];
                                    productTemp[index].service = item;
                                    productTemp[index].service_name =
                                      item.value;
                                    setProductList(productTemp);
                                  }}
                                />
                              )} */}
                                <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "product_id") || getErrorMsg(index, "service_name")}</span>
                              </div>

                              {/* HSN Code (editable) */}
                              <div style={{ minWidth: 0 }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="HSN Code"
                                  value={row.hsnCode ?? ""}
                                  onChange={(e) => {
                                    const productTemp = [...productList];
                                    productTemp[index].hsnCode = sanitizeInput(
                                      e.target.value,
                                    );
                                    setProductList(productTemp);
                                  }}
                                />
                              </div>

                              <div style={{ minWidth: 0 }}>
                                <CommonDropdown
                                  apiUrl={`${apiAccUrl}get_uom?term=`}
                                  placeholder="Select"
                                  value={row.unit}
                                  onSelect={(item) => {
                                    const productTemp = [...productList];
                                    productTemp[index].unit = item;
                                    setProductList(productTemp);
                                  }}
                                />
                                <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "uom_id")}</span>
                              </div>

                              {/* Rest of the input fields */}
                              <div className="d-flex flex-column">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Quantity"
                                  value={row.quantity}
                                  onChange={(e) => {
                                    const productTemp = [...productList];
                                    productTemp[index].quantity = e.target.value.replace(/[^0-9.]/g, "");
                                    setProductList(productTemp);
                                    updateProductTotal(index);
                                  }}
                                />
                                <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "quantity")}</span>
                              </div>

                              <div className="d-flex flex-column">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Unit price"
                                  value={row.unit_without_tax}
                                  onChange={(e) => {
                                    const productTemp = [...productList];
                                    productTemp[index].unit_without_tax = e.target.value.replace(/[^0-9.]/g, "");
                                    setProductList(productTemp);
                                    updateProductTotal(index);
                                  }}
                                />
                                <span className="fs-12 fw-400 text-danger">{getErrorMsg(index, "unit_price")}</span>
                              </div>

                              {/* Amount = Quantity × Unit Price (read-only) */}
                              <div className="d-flex flex-column">
                                <input
                                  type="text"
                                  className="form-control"
                                  readOnly
                                  value={(
                                    (parseFloat(row.quantity) || 0) *
                                    (parseFloat(row.unit_without_tax) || 0)
                                  ).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                />
                              </div>

                              {/* Discount split equally across items (read-only) */}
                              {hasDiscount && (
                                <div className="d-flex flex-column">
                                  <input
                                    type="text"
                                    className="form-control"
                                    readOnly
                                    value={perItemDiscount.toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  />
                                </div>
                              )}

                              {isCgst && (
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="CGST %"
                                  value={row.cgst_percentage}
                                  onChange={(e) => {
                                    const productTemp = [...productList];
                                    productTemp[index].cgst_percentage = e.target.value.replace(/[^0-9.]/g, "");
                                    setProductList(productTemp);
                                    updateProductTotal(index);
                                  }}
                                />
                              )}

                              {isIgst && (
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="IGST %"
                                  value={row.igst_percentage}
                                  onChange={(e) => {
                                    const productTemp = [...productList];
                                    productTemp[index].igst_percentage = e.target.value.replace(/[^0-9.]/g, "");
                                    setProductList(productTemp);
                                    updateProductTotal(index);
                                  }}
                                />
                              )}

                              {isSgst && (
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="SGST %"
                                  value={row.sgst_percentage}
                                  onChange={(e) => {
                                    const productTemp = [...productList];
                                    productTemp[index].sgst_percentage = e.target.value.replace(/[^0-9.]/g, "");
                                    setProductList(productTemp);
                                    updateProductTotal(index);
                                  }}
                                />
                              )}

                              {/* Total Amount with breakdown tooltip */}
                              {(() => {
                                const bd = getItemBreakdown(row);
                                const money = (n) =>
                                  n.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  });
                                const rows = [
                                  ["Amount", money(bd.baseAmount)],
                                  ...(bd.itemDiscount > 0
                                    ? [["Discount", `- ${money(bd.itemDiscount)}`]]
                                    : []),
                                  ["Taxable", money(bd.taxableAmount)],
                                  ...(bd.cgst > 0
                                    ? [[`CGST (${bd.cgst}%)`, `+ ${money(bd.cgstAmount)}`]]
                                    : []),
                                  ...(bd.sgst > 0
                                    ? [[`SGST (${bd.sgst}%)`, `+ ${money(bd.sgstAmount)}`]]
                                    : []),
                                  ...(bd.igst > 0
                                    ? [[`IGST (${bd.igst}%)`, `+ ${money(bd.igstAmount)}`]]
                                    : []),
                                  ["Total", money(bd.total), true],
                                ];
                                return (
                                  <div className="form-control bg-light d-flex align-items-center justify-content-between total-amount-cell">
                                    <span>{money(bd.total)}</span>
                                    <InfoTooltip rows={rows} />
                                  </div>
                                );
                              })()}

                              {/* Delete Button */}
                              <button
                                className="btn btn-link p-0"
                                onClick={() => {
                                  if (row.id) {
                                    setRemovedProductList([...removedProductList, row.id]);
                                  }
                                  const productTemp = [...productList];
                                  productTemp.splice(index, 1);

                                  setProductList(productTemp);
                                  updateProductTotal();
                                }}
                              >
                                <DeleteIC />
                              </button>

                              {/* Error Message */}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-white rounded-4 mt-0 d-flex align-items-center justify-content-center" style={{ height: "100px" }}>
                      <span className="fs-14 fw-400 text-muted">
                        No {itemType === "Product" ? "products" : "services"} selected. Click "Add {itemType === "Product" ? "item" : "service"}" to begin.
                      </span>
                    </div>
                  )}
                </div>

                <div className={`col-xl-12 mt-2 d-flex ${productList.length > 0 ? "justify-content-start" : "justify-content-center"}`}>
                  <Button
                    className="fs-13 fw-600 textblue cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center gap-2"
                    onClick={() => {
                      setProductList([
                        ...productList,
                        {
                          id: 0,
                          product: null,
                          service: null,
                          service_name: "",
                          hsnCode: "",
                          unit: null,
                          quantity: 0,
                          unit_without_tax: 0,
                          cgst_percentage: 0,
                          sgst_percentage: 0,
                          igst_percentage: 0,
                          total_amount: 0,
                        },
                      ]);
                    }}
                    style={{
                      minWidth: "100px",
                      background: "#E7EDFF",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#3D4AF5")}
                    onMouseLeave={(e) => (e.target.style.color = "#3D4AF5")}
                  >
                    <AddNew /> Add {itemType === "Product" ? "item" : "service"}
                  </Button>
                </div>
              </div>
              <div className="row mt-2 align-items-start justify-content-between">
                {/* First Description */}
                <div className="col-md-5 d-flex flex-column gap-1">
                  <div className="fs-13 fw-400 textblk mt-2">Terms & Conditions</div>
                  <textarea
                    value={termsValue}
                    onChange={(e) => setTermsValue(e.target.value)}
                    className="fs-14 fw-400 w-100"
                    style={{
                      borderRadius: "8px",
                      border: "0.6px solid #d4dde7",
                      backgroundColor: "#ffffff",
                      padding: "16px 12px",
                      color: "#172b4d",
                    }}
                    placeholder="Enter here..."
                  />
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.terms_conditions}
                  </span>
                  <div className="fs-13 fw-400 textblk mt-2">Customer Notes</div>
                  <textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    className="fs-14 fw-400 w-100"
                    style={{
                      borderRadius: "8px",
                      border: "0.6px solid #d4dde7",
                      backgroundColor: "#ffffff",
                      padding: "16px 12px",
                      color: "#172b4d",
                    }}
                    placeholder="Enter here..."
                  />{" "}
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {validations.customer_note}
                  </span>
                </div>

                {/* Second Description */}
                {productList.length > 0 && (
                  <div className="col-md-6 d-flex flex-column gap-1">
                    <div
                      style={{
                        backgroundColor: "#F8F8F8",
                        borderRadius: "12px",
                        padding: "12px",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                        marginLeft: "12px",
                        height: "100%",
                      }}
                    >
                      <div className="row-item">
                        <span className="fs-13 fw-400 textblk">Sub Total</span>
                        <span className="fs-14 fw-600 text-dkblue">{currencySymbol}{subTotal.toFixed(2)}</span>
                      </div>
                      <div className="divider"></div>
                      <div className="tds-row">
                        <div className="tds-options">
                          <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                            Discount
                            <InfoTooltip
                              rows={[
                                ["On amount", `${currencySymbol}${Number(subTotal).toFixed(2)}`],
                                [
                                  "Entered",
                                  discountType === "%"
                                    ? `${Number(discountValue) || 0}%`
                                    : `${currencySymbol}${(Number(discountValue) || 0).toFixed(2)}`,
                                ],
                                ["Discount amount", `${currencySymbol}${Number(discountTotal).toFixed(2)}`],
                                ...(productList.length > 0
                                  ? [
                                      [
                                        `Per item (÷${productList.length})`,
                                        `${currencySymbol}${(Number(discountTotal) / productList.length).toFixed(2)}`,
                                      ],
                                    ]
                                  : []),
                              ]}
                            />
                          </span>
                          <div className="combined-input" style={{ marginLeft: "60px" }}>
                            <input
                              type="number"
                              placeholder="Enter value"
                              className="combined-field"
                              value={discountValue}
                              onChange={(e) => {
                                let val = Number(e.target.value);

                                if (discountType === "%") {
                                  // % discount should be between 0 and 100
                                  if (val > 100) val = 100;
                                  if (val < 0) val = 0;
                                } else {
                                  // Rs discount – allow positive numbers only
                                  if (val < 0) val = 0;
                                }

                                setDiscountValue(val);
                                discountCalculate(val);
                              }}
                            />

                            <select
                              className="combined-select"
                              value={discountType}
                              onChange={(e) => {
                                setDiscountType(e.target.value);
                                setDiscountValue(0);
                                discountCalculate(0);
                              }}
                            >
                              <option value="Rs.">Rs.</option>
                              <option value="%">%</option>
                            </select>
                          </div>
                        </div>

                        <span className="fs-14 fw-600 text-dkblue">-{discountTotal.toFixed(2)}</span>
                      </div>
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.discount_amount}
                      </span>
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.discount_percentage}
                      </span>{" "}
                      {/* {totalTax > 0 && ( */}
                        <div className="row-item">
                          <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                            GST
                            {(() => {
                              const t = productList.reduce(
                                (acc, row) => {
                                  const bd = getItemBreakdown(row);
                                  acc.cgst += bd.cgstAmount;
                                  acc.sgst += bd.sgstAmount;
                                  acc.igst += bd.igstAmount;
                                  return acc;
                                },
                                { cgst: 0, sgst: 0, igst: 0 },
                              );
                              const m = (n) => `${currencySymbol}${n.toFixed(2)}`;
                              return (
                                <InfoTooltip
                                  rows={[
                                    [
                                      "On amount",
                                      m(Number(subTotal) - Number(discountTotal)),
                                    ],
                                    ...(t.cgst > 0 ? [["CGST", m(t.cgst)]] : []),
                                    ...(t.sgst > 0 ? [["SGST", m(t.sgst)]] : []),
                                    ...(t.igst > 0 ? [["IGST", m(t.igst)]] : []),
                                    ["Total GST", m(totalTax), true],
                                  ]}
                                />
                              );
                            })()}
                          </span>
                          <span className="fs-14 fw-600 text-dkblue">{currencySymbol}{totalTax.toFixed(2)}</span>
                        </div>
                      {/* )} */}
<div className="tds-row">
                          <div className="tds-options">
                            <span className="label d-inline-flex align-items-center gap-1">
                              Adjustment
                              <InfoTooltip
                                rows={[
                                  ["Adjustment", `${currencySymbol}${(Number(adjustmentValue) || 0).toFixed(2)}`],
                                  ["Applied to", "Grand total"],
                                ]}
                              />
                            </span>
                            <div className="combined-input" style={{ marginLeft: "30px" }}>
                              <input
                                type="number"
                                placeholder={`${currencySymbol} Amount`}
                                className="combined-field-1"
                                value={adjustmentValue}
                                onChange={(e) => {
                                  let val = Number(e.target.value);

                                  setAdjustmentValue(val);
                                }}
                              />
                            </div>
                          </div>
                          <span className="fs-14 fw-600 text-dkblue">{adjustmentValue}</span>

                        </div>
                        <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.adjust_amount}
                      </span>
                      <div className="divider"></div>
                      <div className="row-item total">
                        <span className="fs-15 fw-600 text-dkblue">Total </span>
                        <span className="fs-16 fw-600 text-dkblue">{currencySymbol}{finalTotal.toFixed(2)}</span>
                      </div>

                      {/* Organization currency differs from the selected currency —
                          show the base-currency equivalent (Total × Exchange Rate). */}
                      {showConversionRate && (
                        <div className="row-item">
                          <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                            Total in {orgCurrencyName || "Org Currency"}
                            <InfoTooltip
                              rows={[
                                ["Total Amount", `${currencySymbol}${finalTotal.toFixed(2)}`],
                                ["Exchange Rate", `× ${Number(exchangeRate) || 1}`],
                                [
                                  "Converted Amount",
                                  `${orgCurrencySymbol} ${(finalTotal * (Number(exchangeRate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                  true,
                                ],
                              ]}
                            />
                          </span>
                          <span className="fs-14 fw-600 text-dkblue">
                            {orgCurrencySymbol} {(finalTotal * (Number(exchangeRate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}

                      {/* TDS/TCS + Adjustment shown below the Total — informational
                          only, NOT included in the Total (Rs.) figure above. */}

                      <div className="tds-row">
                        <div className="tds-options">
                          <label className="radio">
                            <input type="radio" name="tds" value="TDS" checked={tdsType === "TDS"} onChange={(e) => setTdsType(e.target.value)} />
                            <span>TDS</span>
                          </label>

                          <label className="radio">
                            <input type="radio" name="tds" value="TCS" checked={tdsType === "TCS"} onChange={(e) => setTdsType(e.target.value)} />
                            <span>TCS</span>
                          </label>

                          <InfoTooltip
                            rows={[
                              ["Type", tdsType],
                              [
                                "On amount",
                                `${currencySymbol}${(Number(subTotal || 0) - Number(discountTotal || 0)).toFixed(2)}`,
                              ],
                              ["Rate", `${Number(tdsPercentage) || 0}%`],
                              [
                                tdsType === "TDS" ? "TDS" : "TCS",
                                `${tdsType === "TDS" ? "- " : "+ "}${currencySymbol}${tdsCalcValue.toFixed(2)}`,
                                true,
                              ],
                            ]}
                          />

                          <div className="combined-input " style={{ marginLeft: "10px" }}>
                            <div className="combined-input">
                              <input
                                type="number"
                                placeholder="Enter %"
                                className="combined-field-1"
                                value={tdsPercentage || ""}
                                onChange={(e) => {
                                  let val = Number(e.target.value);

                                  if (val > 100) val = 100; // max limit
                                  if (val < 0) val = 0; // prevent negative

                                  setTdsPercentage(val);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <span className="fs-14 fw-600 text-dkblue">{tdsType === "TDS" ? ` - ${tdsCalcValue.toFixed(2)}` : ` ${tdsCalcValue.toFixed(2)}`}</span>
                      </div>
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.tds_percentage}
                      </span>
                      <span
                        style={{
                          color: "#ff4444",
                          fontSize: "12px",
                          fontWeight: "400",
                        }}
                      >
                        {validations.tcs_percentage}
                      </span>

                      {/* Receivable = Total ∓ TDS/TCS (TDS subtracts, TCS adds). */}
                      <div className="row-item">
                        <span className="fs-14 fw-600 text-dkblue">Receivable</span>
                        <span className="fs-15 fw-700 text-dkblue">
                          {currencySymbol}{(tdsType === "TCS" ? finalTotal + tdsCalcValue : finalTotal - tdsCalcValue).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Attachments Section */}
                      <div style={{ marginTop: "20px", marginBottom: "20px", paddingTop: "20px", borderTop: "1px solid #e5e5e5" }}>
                        <span className="label fs-14 fw-600" style={{ display: "block", marginBottom: "12px" }}>Attachments</span>

                        {/* Attachment Upload Error Alert */}
                        {attachmentUploadError && (
                          <div
                            style={{
                              padding: "12px 16px",
                              marginBottom: "16px",
                              backgroundColor: "#fef2f2",
                              border: "1px solid #fecaca",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                            }}
                          >
                            <div style={{ color: "#dc2626", fontSize: "18px", marginTop: "2px" }}>⚠</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: "#7f1d1d", fontSize: "14px", fontWeight: "500" }}>
                                Attachment Upload Error
                              </div>
                              <div style={{ color: "#991b1b", fontSize: "13px", marginTop: "4px" }}>
                                {attachmentUploadError}
                              </div>
                              {currentQuotationId && (
                                <div style={{ color: "#7f1d1d", fontSize: "12px", marginTop: "8px", fontStyle: "italic" }}>
                                  Quotation created (ID: {currentQuotationId}). Click "Save & Continue" button above to retry uploading attachments.
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => setAttachmentUploadError(null)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#dc2626",
                                fontSize: "18px",
                                cursor: "pointer",
                                padding: "0",
                              }}
                              title="Dismiss"
                            >
                              ×
                            </button>
                          </div>
                        )}

                        <FileUploadComponent
                          uploadedFiles={uploadedFiles}
                          onFilesAdd={handleFilesAdd}
                          onFileRemove={handleFileRemove}
                          maxSize={5}
                          acceptedFormats=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        />
                      </div>
            </div>
          </Col>
        </Row>
      </div>
      <Modal show={showSwitchModal} onHide={cancelItemTypeChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>If you switch tab, current tab values will be removed</Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={cancelItemTypeChange}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmItemTypeChange}>
            Switch
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Attachment Delete Modal */}
      <AttachmentDeleteModal
        isOpen={attachmentDeleteModal}
        attachment={attachmentToDelete}
        isDeleting={isDeleting}
        onConfirm={confirmDeleteAttachment}
        onCancel={cancelDeleteAttachment}
      />

      {/* Validity / Edit Remarks Modal */}
      <Modal
        show={showRemarksModal}
        onHide={() => setShowRemarksModal(false)}
        centered
        backdrop="static"
        contentClassName="border-0 shadow"
        style={{ fontFamily: "'Euclid Circular B', sans-serif" }}
      >
        <Modal.Header
          style={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #EAEAEA",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="fs-16 fw-600 text-dkblue">Quotation Modification Reason</div>
          <button
            type="button"
            onClick={() => setShowRemarksModal(false)}
            style={{
              background: "none",
              border: "none",
              color: "#6c757d",
              fontSize: "20px",
              lineHeight: "1",
              cursor: "pointer",
              padding: "0 4px",
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          {/* Modified Fields Summary Card */}
          <div
            style={{
              backgroundColor: "#F4F6F8",
              border: "1px solid #E5E9EB",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "16px",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fs-12 fw-600 text-dkblue">
                Modified Field(s) detected:
              </div>
              <div className="fs-11 text-grey">
                Hover on a field to see previous & updated values
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {modifiedFieldsList.length > 0 ? (
                modifiedFieldsList.map((field, idx) => (
                  <ChangeBadgeWithTooltip key={idx} change={field} />
                ))
              ) : (
                <span className="fs-12 text-grey">General quotation update</span>
              )}
            </div>
          </div>

          <div className="fs-13 fw-500 text-dark mb-1">
            Reason for Editing <span className="text-danger">*</span>
          </div>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Enter reason for modifying this quotation..."
            value={editRemarks}
            onChange={(e) => {
              setEditRemarks(e.target.value);
              if (remarksError) setRemarksError("");
            }}
            maxLength={500}
            style={{
              borderRadius: "8px",
              padding: "10px 12px",
              fontSize: "14px",
              borderColor: remarksError ? "#dc3545" : "#D4DDE7",
            }}
          />
          <div className="d-flex justify-content-between align-items-center mt-1">
            {remarksError ? (
              <span className="text-danger" style={{ fontSize: "12px" }}>
                {remarksError}
              </span>
            ) : (
              <span className="text-grey" style={{ fontSize: "11px" }}>
                This reason and change summary will be recorded in quotation history.
              </span>
            )}
            <span className="text-grey" style={{ fontSize: "12px" }}>
              {editRemarks.length}/500
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: "1px solid #EAEAEA",
            padding: "14px 24px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
          }}
        >
          <Button
            variant="light"
            onClick={() => setShowRemarksModal(false)}
            style={{
              backgroundColor: "#FFFFFF",
              color: "#0E1B2E",
              border: "1px solid #D4DDE7",
              borderRadius: "100px",
              fontWeight: 600,
              fontSize: "14px",
              padding: "8px 24px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemarksSubmit}
            style={{
              backgroundColor: "#3D4AF5",
              borderColor: "#3D4AF5",
              color: "#FFFFFF",
              borderRadius: "100px",
              fontWeight: 600,
              fontSize: "14px",
              padding: "8px 24px",
            }}
          >
            Confirm & Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default QuotationCreate;
